// 고객센터 / 1:1 문의 + 약관·정책 + 회원 탈퇴.
// 작업계획서 4.6 + Phase 0 결정: 1:1 문의는 앱 내 메시지가 아니라 전화/카카오 채널 연결.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle, Phone } from "lucide-react";
import { useConfirm } from "@oo/ui";
import { useStoreSettings } from "@/features/operations/useStoreSettings.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import { withdrawAccount } from "@/features/auth/authApi.ts";

// 전화번호를 tel: 링크용으로 정리 (하이픈/공백 제거).
function telHref(phone: string): string {
  return `tel:${phone.replace(/[^0-9+]/gu, "")}`;
}

// 고객지원/문의 화면 (전화/카카오 연결).
export default function SupportPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const ops = useStoreSettings();
  const clearSession = useAuthStore((s) => s.clearSession);
  const isLoggedIn = useAuthStore((s) => !!s.session);
  const [withdrawing, setWithdrawing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const phone = ops.settings?.contactPhone ?? null;
  const kakao = ops.settings?.kakaoUrl ?? null;

  async function onWithdraw() {
    const ok = await confirm({
      title: "회원 탈퇴",
      description: "정말 탈퇴하시겠어요? 적립금/쿠폰이 모두 사라지며 되돌릴 수 없습니다.",
      confirmLabel: "탈퇴",
    });
    if (!ok) {
      return;
    }
    setWithdrawing(true);
    setError(null);
    try {
      await withdrawAccount();
      clearSession();
      navigate("/", { replace: true });
    } catch {
      setError("탈퇴 처리에 실패했습니다. 잠시 후 다시 시도해 주세요.");
      setWithdrawing(false);
    }
  }

  return (
    <main className="mx-auto flex w-full max-w-screen-sm flex-col gap-5 px-4 py-6 pb-28">
      <h1 className="text-2xl font-bold">고객센터</h1>

      {/* 1:1 문의 */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-ink-700">1:1 문의</h2>
        {phone || kakao ? (
          <div className="flex flex-col gap-2">
            {phone ? (
              <a
                href={telHref(phone)}
                className="flex h-12 items-center justify-between rounded-md bg-brand-600 px-4 text-base font-semibold text-white"
              >
                <span className="flex items-center gap-2">
                  <Phone size={18} aria-hidden="true" />
                  전화 문의
                </span>
                <span className="tabular-nums">{phone}</span>
              </a>
            ) : null}
            {kakao ? (
              <a
                href={kakao}
                target="_blank"
                rel="noreferrer"
                className="flex h-12 items-center justify-center gap-2 rounded-md bg-yellow-300 text-base font-semibold text-ink-900"
              >
                <MessageCircle size={18} aria-hidden="true" />
                카카오톡 문의
              </a>
            ) : null}
          </div>
        ) : (
          <p className="rounded-md border border-ink-200 bg-ink-50 p-3 text-sm text-ink-600">
            문의 채널이 아직 등록되지 않았습니다. 매장에 직접 문의해 주세요.
          </p>
        )}
      </section>

      {/* 약관 및 정책 - 정식 정책 페이지로 연결(인앱 결제 도입에 맞춰 갱신). */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-ink-700">약관 및 정책</h2>
        <div className="flex flex-col divide-y divide-ink-200 overflow-hidden rounded-md border border-ink-200 bg-white text-sm">
          {[
            { to: "/legal/terms", label: "이용약관" },
            { to: "/legal/privacy", label: "개인정보처리방침" },
            { to: "/legal/refund", label: "취소·환불·교환 정책" },
            { to: "/legal/business", label: "사업자 정보" },
          ].map((it) => (
            <Link
              key={it.to}
              to={it.to}
              className="flex items-center justify-between px-3 py-3 hover:bg-ink-50"
            >
              <span className="font-medium text-ink-800">{it.label}</span>
              <span className="text-ink-400">{">"}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* 회원 탈퇴 */}
      {isLoggedIn ? (
        <section className="flex flex-col gap-2 pt-2">
          {error ? <p className="text-sm text-danger-700">{error}</p> : null}
          <button
            type="button"
            onClick={onWithdraw}
            disabled={withdrawing}
            className="h-11 rounded-md border border-ink-300 text-sm text-ink-600 hover:bg-ink-100 disabled:opacity-50"
          >
            {withdrawing ? "처리 중..." : "회원 탈퇴"}
          </button>
        </section>
      ) : null}

      <Link to="/me" className="text-center text-sm text-ink-600 underline">
        마이페이지로
      </Link>
    </main>
  );
}
